import { LitElement, html, css } from 'lit';

class NavBar extends LitElement {
  static properties = {
    currentPath: { type: String, attribute: 'current-path' },
  };

  static styles = css`
    :host { display: block; }

    /* Desktop sidebar */
    .sidebar {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 16rem;
      height: 100%;
      background: white;
      border-right: 1px solid #E5E7EB;
      padding: 1.5rem 0;
      z-index: 40;
    }

    @media (min-width: 768px) {
      .sidebar { display: block; }
      .mobile-nav { display: none !important; }
    }

    .logo {
      padding: 0 1.5rem;
      margin-bottom: 2rem;
      font-size: 1.25rem;
      font-weight: 700;
      color: #111827;
    }

    .nav-items { display: flex; flex-direction: column; gap: 0.25rem; padding: 0 0.75rem; }

    .nav-link {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.625rem 0.75rem;
      border-radius: 0.5rem;
      color: #374151;
      text-decoration: none;
      font-size: 0.875rem;
      font-weight: 500;
      transition: background-color 0.15s, color 0.15s;
    }
    .nav-link:hover { background: #F3F4F6; }
    .nav-link.active { background: #EFF6FF; color: #2563EB; }

    .nav-link svg { width: 1.25rem; height: 1.25rem; flex-shrink: 0; }

    .signout-section {
      position: absolute;
      bottom: 1.5rem;
      left: 0;
      right: 0;
      padding: 0 1.5rem;
    }

    .signout-btn {
      width: 100%;
      padding: 0.5rem;
      border: 1px solid #E5E7EB;
      border-radius: 0.5rem;
      background: none;
      color: #6B7280;
      font-size: 0.875rem;
      cursor: pointer;
    }
    .signout-btn:hover { background: #F3F4F6; color: #DC2626; }

    /* Mobile bottom nav */
    .mobile-nav {
      display: flex;
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: white;
      border-top: 1px solid #E5E7EB;
      z-index: 40;
      justify-content: space-around;
      padding: 0.5rem 0;
    }

    .mobile-link {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.125rem;
      padding: 0.25rem;
      color: #6B7280;
      text-decoration: none;
      font-size: 0.625rem;
    }
    .mobile-link.active { color: #2563EB; }
    .mobile-link svg { width: 1.25rem; height: 1.25rem; }
  `;

  constructor() {
    super();
    this.currentPath = '/';
  }

  _isActive(path) {
    if (path === '/') return this.currentPath === '/';
    return this.currentPath.startsWith(path);
  }

  _signOut() {
    this.dispatchEvent(new CustomEvent('sign-out', { bubbles: true, composed: true }));
  }

  render() {
    const navItems = [
      { path: '/', label: 'Home', icon: this._homeIcon },
      { path: '/recipes', label: 'Recipes', icon: this._recipesIcon },
      { path: '/planner', label: 'Planner', icon: this._plannerIcon },
      { path: '/shopping', label: 'Shopping', icon: this._shoppingIcon },
    ];

    return html`
      <!-- Desktop Sidebar -->
      <nav class="sidebar no-print">
        <div class="logo">Meal Planner</div>
        <div class="nav-items">
          ${navItems.map(item => html`
            <a href="#${item.path}" class="nav-link ${this._isActive(item.path) ? 'active' : ''}">
              ${item.icon}
              <span>${item.label}</span>
            </a>
          `)}
          <a href="#/settings" class="nav-link ${this._isActive('/settings') ? 'active' : ''}">
            ${this._settingsIcon}
            <span>Settings</span>
          </a>
        </div>
        <div class="signout-section">
          <button class="signout-btn" @click=${this._signOut}>Sign Out</button>
        </div>
      </nav>

      <!-- Mobile Bottom Nav -->
      <nav class="mobile-nav no-print">
        ${navItems.slice(0, 5).map(item => html`
          <a href="#${item.path}" class="mobile-link ${this._isActive(item.path) ? 'active' : ''}">
            ${item.icon}
            <span>${item.label}</span>
          </a>
        `)}
      </nav>
    `;
  }

  get _homeIcon() {
    return html`<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/></svg>`;
  }
  get _recipesIcon() {
    return html`<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg>`;
  }
  get _plannerIcon() {
    return html`<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>`;
  }
  get _shoppingIcon() {
    return html`<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/></svg>`;
  }
  get _inventoryIcon() {
    return html`<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>`;
  }
  get _budgetIcon() {
    return html`<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`;
  }
  get _settingsIcon() {
    return html`<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>`;
  }
}

customElements.define('nav-bar', NavBar);
